'use client'

import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Smartphone,
  Users,
  Guitar,
  ArrowLeft,
  Tablet,
  Rocket,
  Sparkles,
} from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { DemoProvider, useDemo } from './demo-context'
import { useDemoSimulation } from './use-demo-simulation'
import { useDemoAnalytics } from './use-demo-analytics'
import { trackEvent } from '@/lib/analytics'
import { DemoAudienceView } from './audience/demo-audience-view'
import { DemoArtistConsole } from './artist/demo-artist-console'
import { CoachMarkProvider } from './coach-marks/coach-mark-provider'
import { CoachMarksStateProvider, useCoachMarks } from './coach-marks/use-coach-marks'
import { DemoSessionProvider, useDemoSession } from './use-demo-session-tracking'

// Hook to detect mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024) // lg breakpoint
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  return isMobile
}

// Wrapper that activates simulation + analytics
function DemoSimulationWrapper({ children }: { children: React.ReactNode }) {
  const { state } = useDemo()
  useDemoSimulation()
  useDemoAnalytics(state)
  return (
    <DemoSessionProvider>
      {children}
    </DemoSessionProvider>
  )
}

// Floating nudge to switch views on mobile when a coach mark targets the hidden view
function MobileViewNudge({
  activeView,
  onSwitchView,
}: {
  activeView: 'audience' | 'artist'
  onSwitchView: (view: 'audience' | 'artist') => void
}) {
  const { activeCoachMark } = useCoachMarks()
  const t = useTranslations('demo')

  if (!activeCoachMark) return null
  // Only show nudge if the mark targets the OTHER view
  if (activeCoachMark.view === activeView || activeCoachMark.view === 'both') return null

  const targetView = activeCoachMark.view as 'audience' | 'artist'
  const isArtist = targetView === 'artist'

  return (
    <button
      onClick={() => onSwitchView(targetView)}
      className={`absolute -top-12 z-50 animate-bounce px-3 py-1.5 rounded-full shadow-lg text-xs font-medium flex items-center gap-1.5 ${
        isArtist
          ? 'right-2 bg-amber-500/90 text-white'
          : 'left-2 bg-purple-500/90 text-white'
      }`}
    >
      {isArtist ? (
        <>{t('switchToArtist')} <Guitar className="h-3 w-3" /></>
      ) : (
        <><Users className="h-3 w-3" /> {t('switchToAudience')}</>
      )}
    </button>
  )
}

// Mobile view with toggle
function MobileDemo() {
  const [activeView, setActiveView] = useState<'audience' | 'artist'>('audience')
  const mobileContentRef = useRef<HTMLDivElement>(null)
  const demoSession = useDemoSession()
  const t = useTranslations('demo')

  return (
    <div className="h-screen flex flex-col bg-zinc-950">
      {/* Main content area */}
      <div ref={mobileContentRef} className="flex-1 overflow-hidden relative">
        {activeView === 'audience' ? (
          <DemoAudienceView />
        ) : (
          <DemoArtistConsole />
        )}
        <CoachMarkProvider containerRef={mobileContentRef} view={activeView} />
      </div>

      {/* Bottom toggle bar */}
      <div className="shrink-0 border-t border-zinc-800 bg-zinc-900/95 backdrop-blur-sm p-2 safe-area-inset-bottom relative">
        <MobileViewNudge activeView={activeView} onSwitchView={setActiveView} />
        <div className="flex gap-2">
          <Button
            variant={activeView === 'audience' ? 'default' : 'outline'}
            size="sm"
            className={`flex-1 ${
              activeView === 'audience'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600'
                : 'border-zinc-700 hover:bg-zinc-800'
            }`}
            onClick={() => setActiveView('audience')}
          >
            <Users className="h-4 w-4 mr-1" />
            <span className="text-xs">{t('audienceTab')}</span>
          </Button>

          {/* CTA Button - Center */}
          <Link href="/signup" className="shrink-0" onClick={() => { trackEvent('demo_cta_clicked', { location: 'mobile_bottom_bar' }); demoSession?.trackMilestone('cta_clicked', { location: 'mobile_bottom_bar' }) }}>
            <Button
              size="sm"
              className="bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 hover:from-emerald-400 hover:via-green-400 hover:to-emerald-400 text-white font-semibold shadow-lg shadow-green-500/30 animate-pulse hover:animate-none px-4"
            >
              <Rocket className="h-4 w-4 mr-1" />
              <span className="text-xs">{t('getStarted')}</span>
            </Button>
          </Link>

          <Button
            variant={activeView === 'artist' ? 'default' : 'outline'}
            size="sm"
            className={`flex-1 ${
              activeView === 'artist'
                ? 'bg-gradient-to-r from-amber-600 to-orange-600'
                : 'border-zinc-700 hover:bg-zinc-800'
            }`}
            onClick={() => setActiveView('artist')}
          >
            <Guitar className="h-4 w-4 mr-1" />
            <span className="text-xs">{t('artistTab')}</span>
          </Button>
        </div>
      </div>
    </div>
  )
}

// Desktop view with side-by-side split
function DesktopDemo() {
  const [artistViewMode, setArtistViewMode] = useState<'tablet' | 'phone'>('tablet')
  const audienceFrameRef = useRef<HTMLDivElement>(null)
  const artistFrameRef = useRef<HTMLDivElement>(null)
  const demoSession = useDemoSession()
  const t = useTranslations('demo')

  return (
    <div className="h-screen flex flex-col bg-zinc-950">
      {/* Top Header */}
      <div className="shrink-0 border-b border-zinc-800 bg-zinc-900/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                href="/"
                className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">{t('backToHome')}</span>
              </Link>
              <div className="h-4 w-px bg-zinc-700" />
              <div className="flex items-center gap-2">
                <Badge className="bg-gradient-to-r from-purple-600 to-pink-600">
                  {t('interactiveDemo')}
                </Badge>
                <span className="text-sm text-zinc-400">{t('experienceIPerform')}</span>
              </div>
            </div>

            <Link href="/signup" onClick={() => { trackEvent('demo_cta_clicked', { location: 'desktop_header' }); demoSession?.trackMilestone('cta_clicked', { location: 'desktop_header' }) }}>
              <Button
                className="bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 hover:from-emerald-400 hover:via-green-400 hover:to-emerald-400 text-white font-semibold shadow-lg shadow-green-500/30"
              >
                <Rocket className="h-4 w-4 mr-2" />
                {t('getStarted')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main split view */}
      <div className="flex-1 flex overflow-hidden">
        {/* Audience View - Left side (narrower) */}
        <div className="w-[420px] shrink-0 flex flex-col border-r border-zinc-800">
          <div className="shrink-0 h-10 bg-zinc-800/50 px-4 border-b border-zinc-700 flex items-center">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-purple-400" />
              <span className="text-sm font-medium text-purple-400">{t('audienceView')}</span>
              <span className="text-xs text-zinc-500">{t('audienceViewMobile')}</span>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            {/* Phone frame mockup */}
            <div className="h-full flex items-center justify-center p-4 bg-zinc-950/50">
              <div ref={audienceFrameRef} className="w-full max-w-[380px] h-full max-h-[780px] bg-zinc-900 rounded-[2.5rem] border-[8px] border-zinc-800 shadow-2xl overflow-hidden relative">
                {/* Phone notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-zinc-800 rounded-b-2xl z-10" />
                {/* Content */}
                <div className="h-full pt-6 overflow-hidden">
                  <DemoAudienceView />
                </div>
                <CoachMarkProvider containerRef={audienceFrameRef} view="audience" />
              </div>
            </div>
          </div>
        </div>

        {/* Artist View - Right side (wider) */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="shrink-0 h-10 bg-zinc-800/50 px-4 border-b border-zinc-700 flex items-center">
            <div className="flex items-center justify-between flex-1">
              <div className="flex items-center gap-2">
                <Guitar className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-400">{t('artistConsole')}</span>
                <span className="text-xs text-zinc-500">{t('whatPerformersSee')}</span>
              </div>
              {/* View mode toggle */}
              <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-0.5">
                <button
                  onClick={() => setArtistViewMode('tablet')}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    artistViewMode === 'tablet'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <Tablet className="h-3.5 w-3.5" />
                  {t('tablet')}
                </button>
                <button
                  onClick={() => setArtistViewMode('phone')}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    artistViewMode === 'phone'
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <Smartphone className="h-3.5 w-3.5" />
                  {t('phone')}
                </button>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            {artistViewMode === 'tablet' ? (
              /* Tablet frame mockup */
              <div className="h-full flex items-center justify-center p-4 bg-zinc-950/50">
                <div ref={artistFrameRef} className="w-full max-w-[768px] h-full max-h-[1024px] bg-zinc-900 rounded-[1.5rem] border-[6px] border-zinc-800 shadow-2xl overflow-hidden relative">
                  {/* Tablet camera */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-zinc-700 rounded-full z-10" />
                  {/* Content */}
                  <div className="h-full pt-1 overflow-hidden">
                    <DemoArtistConsole />
                  </div>
                  <CoachMarkProvider containerRef={artistFrameRef} view="artist" />
                </div>
              </div>
            ) : (
              /* Phone frame mockup */
              <div className="h-full flex items-center justify-center p-4 bg-zinc-950/50">
                <div ref={artistFrameRef} className="w-full max-w-[380px] h-full max-h-[780px] bg-zinc-900 rounded-[2.5rem] border-[8px] border-zinc-800 shadow-2xl overflow-hidden relative">
                  {/* Phone notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-zinc-800 rounded-b-2xl z-10" />
                  {/* Content */}
                  <div className="h-full pt-6 overflow-hidden">
                    <DemoArtistConsole />
                  </div>
                  <CoachMarkProvider containerRef={artistFrameRef} view="artist" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating CTA Button - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-50">
        <Link href="/signup" onClick={() => { trackEvent('demo_cta_clicked', { location: 'desktop_floating' }); demoSession?.trackMilestone('cta_clicked', { location: 'desktop_floating' }) }}>
          <Button
            size="lg"
            className="bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 hover:from-emerald-400 hover:via-green-400 hover:to-emerald-400 text-white font-semibold shadow-xl shadow-green-500/40 hover:shadow-green-500/60 transition-all hover:scale-105 group"
          >
            <Sparkles className="h-5 w-5 mr-2 group-hover:animate-pulse" />
            {t('getStarted')}
          </Button>
        </Link>
      </div>
    </div>
  )
}

export default function DemoPage() {
  const isMobile = useIsMobile()
  const [mounted, setMounted] = useState(false)
  const t = useTranslations('demo')


  /* eslint-disable react-hooks/set-state-in-effect -- tracking client-side mount for SSR hydration */
  useEffect(() => {
    setMounted(true)
  }, [])
  /* eslint-enable react-hooks/set-state-in-effect */

  if (!mounted) {
    // Loading state that matches both layouts
    return (
      <div className="h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">{t('loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <DemoProvider>
      <CoachMarksStateProvider>
        <DemoSimulationWrapper>
          {isMobile ? <MobileDemo /> : <DesktopDemo />}
        </DemoSimulationWrapper>
      </CoachMarksStateProvider>
    </DemoProvider>
  )
}
